import React, { useState } from "react";
import { InputsContainer } from "../../components/ProponentesField/styles";
import InputField from "../../components/Inputs/InputField";
import Botao from "../../components/Botao/Botao";
import { validaCPF } from "../../utils/validacoes";
import { mascaraCPF } from "../../utils/mascaras";
import CrednetService, {
  CrednetResponse,
  OcorrenciaSection,
  OcorrenciaDetalhe,
} from "../../services/CrednetService";
import Snackbars from '../../components/Snackbars'
import {
  Container,
  Title,
  Wrapper,
  InputsWrapper,
  Card,
  CardText,
  SectionTitle,
  StatusRow,
  StatusLabel,
  StatusBadge,
  ScoreBox,
  ScoreValue,
  ScoreRisk,
  ScoreDescription,
  OcorrenciaResumoText,
} from "./styles";
import sleep from "../../utils/sleep";

const OCORRENCIAS: { keys: (keyof CrednetResponse)[]; label: string }[] = [
  { keys: ["pendencias_internas"], label: "Pendências Internas" },
  { keys: ["pendencias_financeiras"], label: "Pendências Financeiras" },
  // A SCC pode retornar o protesto sob "protesto_nacional" (adicional "Anotações Completas")
  { keys: ["protesto_estadual", "protesto_nacional"], label: "Protesto" },
  { keys: ["cheques_sem_fundos"], label: "Cheques sem Fundos" },
  { keys: ["alerta_documentos_roubados"], label: "Documentos Roubados" },
  { keys: ["participacao_societaria"], label: "Participação Societária" },
];

const MAX_DETALHES_EXIBIDOS = 5;

const formatMoeda = (valor: number) =>
  valor.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

const formatDetalheLinha = (detalhe: OcorrenciaDetalhe): string => {
  if (detalhe.nome_empresa) {
    const percentual =
      typeof detalhe.percentual_participacao === "number"
        ? ` (${detalhe.percentual_participacao}%)`
        : "";
    return `${detalhe.nome_empresa}${percentual}`;
  }

  if (detalhe.tipo_documento) {
    return `${detalhe.tipo_documento}: ${detalhe.num_documento}`;
  }

  const valor = detalhe.vlr_pendencia ?? detalhe.vlr_protesto ?? detalhe.vlr_cheque;
  const partes: string[] = [];
  if (detalhe.dt_ocorrencia) partes.push(detalhe.dt_ocorrencia);
  if (typeof valor === "number") partes.push(formatMoeda(valor));
  if (detalhe.origem) partes.push(String(detalhe.origem));
  return partes.join(" · ");
};

const getResumoList = (
  resumo: OcorrenciaSection["resumo"]
): OcorrenciaResumo[] => {
  if (Array.isArray(resumo)) return resumo;
  if (resumo) return [resumo];
  return [];
};

const getOcorrenciaResumo = (section: OcorrenciaSection): string[] => {
  const resumos = getResumoList(section.resumo);

  if (resumos.length > 0) {
    return resumos.map((resumo) => {
      const partes: string[] = [];
      if (resumo.tipo_anotacao) partes.push(resumo.tipo_anotacao);
      if (typeof resumo.total_ocorrencias === "number")
        partes.push(`${resumo.total_ocorrencias} ocorrência(s)`);
      if (typeof resumo.vlr_total === "number")
        partes.push(formatMoeda(resumo.vlr_total));
      return partes.join(" · ");
    });
  }

  if (section.detalhes && section.detalhes.length > 0) {
    const linhas = section.detalhes
      .slice(0, MAX_DETALHES_EXIBIDOS)
      .map(formatDetalheLinha);

    const restantes = section.detalhes.length - MAX_DETALHES_EXIBIDOS;
    if (restantes > 0) linhas.push(`+ ${restantes} outra(s) ocorrência(s)`);

    return linhas;
  }

  return [];
};

const Divida = () => {
  const [clientData, setClientData] = useState<CrednetResponse | undefined>(
    undefined
  );
  const [clientCPF, setClientCPF] = useState("");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    type: "success" as "success" | "error" | "info" | "warning",
  });

  const showSnackbar = (
    message: string,
    type: "success" | "error" | "info" | "warning" = "success"
  ) => {
    setSnackbar({ open: true, message, type });
  };

  const handleSnackbarClose = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const handleChangeCPF = (e: React.ChangeEvent<HTMLInputElement>) => {
    setError("");
    setClientCPF(mascaraCPF(e.target.value));
  };

  const handleClientDebtSearch = async () => {
    if (!clientCPF) {
      setError("Digite um CPF.");
      return;
    }

    if (!validaCPF(clientCPF)) {
      setError("CPF inválido.");
      return;
    }

    try {
      setIsLoading(true);
      const findClientDebt = await CrednetService.findClientDebtByCPF(
        clientCPF
      );

      if (!findClientDebt) return;

      setClientData(findClientDebt);

      showSnackbar("Consulta realizada com sucesso!", "success");
      await sleep(2000);
      handleSnackbarClose();
    } catch (err: any) {
      const message =
        err?.message?.response?.data?.message ||
        "Não foi possível concluir a consulta.";
      showSnackbar(message, "error");
      setClientData(undefined);
    } finally {
      setIsLoading(false);
    }
  };

  const confirmei = clientData?.confirmei?.detalhe;
  const score = clientData?.serasa_score_positivo?.detalhe;
  const consultas = clientData?.consultas_serasa?.consultas_sem_cheques;

  return (
    <Container>
      <Title>Divida</Title>

      <Wrapper>
        <InputsContainer>
          <InputsWrapper>
            <InputField
              tipo="formulario"
              className="inputs"
              label="CPF"
              value={clientCPF}
              onChange={handleChangeCPF}
              width={180}
              error={!!error}
              helperText={error}
            />
            <Botao
              tipo="detalhes"
              tamanho="medio"
              onClick={handleClientDebtSearch}
              isLoading={isLoading}
            >
              Buscar
            </Botao>
          </InputsWrapper>
        </InputsContainer>

        {clientData && (
          <Card>
            {confirmei && (
              <>
                <CardText>{confirmei.nome_razao}</CardText>
                <CardText>Situação: {confirmei.situacao_doc}</CardText>
              </>
            )}

            {score && (
              <>
                <SectionTitle>Score Serasa</SectionTitle>
                <ScoreBox>
                  <ScoreValue>{score.pontuacao}</ScoreValue>
                  <ScoreRisk>Risco: {score.risco}</ScoreRisk>
                </ScoreBox>
                <ScoreDescription>{score.desc_risco}</ScoreDescription>
              </>
            )}

            <SectionTitle>Ocorrências</SectionTitle>
            {OCORRENCIAS.map(({ keys, label }) => {
              const section = keys
                .map((key) => clientData[key])
                .find((value) => !!value) as OcorrenciaSection | undefined;

              if (!section) return null;

              const hasOcorrencia = !section.nada_consta;
              const resumoLinhas = hasOcorrencia
                ? getOcorrenciaResumo(section)
                : [];

              return (
                <React.Fragment key={keys[0]}>
                  <StatusRow>
                    <StatusLabel>{label}</StatusLabel>
                    <StatusBadge alert={hasOcorrencia}>
                      {hasOcorrencia ? "Consta ocorrência" : "Nada consta"}
                    </StatusBadge>
                  </StatusRow>
                  {resumoLinhas.map((linha) => (
                    <OcorrenciaResumoText key={`${keys[0]}-${linha}`}>
                      {linha}
                    </OcorrenciaResumoText>
                  ))}
                </React.Fragment>
              );
            })}

            {consultas && (
              <>
                <SectionTitle>Consultas Serasa</SectionTitle>
                <StatusRow>
                  <StatusLabel>Últimos 15 dias</StatusLabel>
                  <StatusLabel>{consultas.qtd_consultas_15_dias}</StatusLabel>
                </StatusRow>
                <StatusRow>
                  <StatusLabel>Últimos 30 dias</StatusLabel>
                  <StatusLabel>{consultas.qtd_consultas_30_dias}</StatusLabel>
                </StatusRow>
                <StatusRow>
                  <StatusLabel>Últimos 60 dias</StatusLabel>
                  <StatusLabel>{consultas.qtd_consultas_60_dias}</StatusLabel>
                </StatusRow>
                <StatusRow>
                  <StatusLabel>Últimos 90 dias</StatusLabel>
                  <StatusLabel>{consultas.qtd_consultas_90_dias}</StatusLabel>
                </StatusRow>
              </>
            )}
          </Card>
        )}
      </Wrapper>

      <Snackbars
        type={snackbar.type}
        handleClose={handleSnackbarClose}
        open={snackbar.open}
      >
        {snackbar.message}
      </Snackbars>
    </Container>
  );
};

export default Divida;
